import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Builds a signature string from ordered media IDs inside a gallery element.
   * Used to detect whether the media set/order actually changed between variants.
   *
   * @param {Element} gallery - The media gallery element.
   * @returns {string} A comma-joined string of media element IDs.
   */
  #getMediaSignature(gallery) {
    const items = gallery.querySelectorAll('[id^="product-"]');
    return Array.from(items, (el) => el.id).join(',');
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * Skips replacement when the media set/order is unchanged (e.g. size-only variant changes)
   * to prevent mobile slideshow teardown/reinit flicker.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Compare media signatures — skip replacement if media set/order is identical
    // and this is not a combined-listing product swap
    if (!event.detail.data.newProduct && this.#getMediaSignature(this) === this.#getMediaSignature(newMediaGallery)) {
      return;
    }

    // Preserve parent height during replacement to prevent layout collapse on mobile
    const parent = this.parentElement;
    if (parent) {
      parent.style.minHeight = this.getBoundingClientRect().height + 'px';
    }

    this.replaceWith(newMediaGallery);

    // Remove temporary min-height after the new gallery renders
    if (parent) {
      requestAnimationFrame(() => {
        parent.style.minHeight = '';
      });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
